import { PRIMARY_BUTTON } from "@/constants";
import { useState } from "react";
import { MdOutlineKeyboardDoubleArrowUp } from "react-icons/md";
import { AnimatePresence, motion } from "motion/react";
import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { IEMRSectionSearchResponseData } from "@/api_framework/api_modals/EMRs";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";

export type IDiagnosticAddOn = IEMRSectionSearchResponseData;

interface DiagnosticAddOnsProps {
  diagnosticAddOns?: IDiagnosticAddOn[];
  onAddItem: (item: IDiagnosticAddOn, source: string) => void;
  petName?: string;
  addedLabTests?: Array<{ lab_test_id?: string | null }>;
  customerId?: string;
  petId?: string;
  emrId?: string;
  onDeleteAddon?: (labId: string) => void;
}

const DiagnosticAddOnsComponent = ({
  diagnosticAddOns,
  onAddItem,
  petName,
  addedLabTests,
  customerId,
  petId,
  emrId,
  onDeleteAddon,
}: DiagnosticAddOnsProps) => {
  const { execute: logAPI } = useClickLogAPI();
  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  const [deletedDiagnosticIds, setDeletedDiagnosticIds] = useState<string[]>([]);
  const [deleteAddon, setDeleteAddon] = useState<IDiagnosticAddOn | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteError, setDeleteError] = useState("");

  const addedLabTestIds = new Set(
    (addedLabTests ?? []).map((item) => item?.lab_test_id).filter(Boolean),
  );
  const visibleAddOns = (diagnosticAddOns ?? []).filter(
    (addOn) =>
      addOn?.lab_test_id &&
      !addedLabTestIds.has(addOn.lab_test_id) &&
      !deletedDiagnosticIds.includes(addOn.lab_test_id),
  );

  if (visibleAddOns.length === 0) return null;

  const closeDeleteModal = () => {
    setDeleteAddon(null);
    setDeleteReason("");
    setDeleteError("");
  };

  const handleDelete = () => {
    const reason = deleteReason.trim();
    if (!reason) {
      setDeleteError("Reason is required to delete recommendation");
      return;
    }

    const labId = deleteAddon?.lab_test_id;
    if (!labId) return;

    const logBody: IClickLogBody = {
      event_type: "EMR_ITEM_DELETED",
      scr_id: "EMR",
      client_id: customerId,
      emr_id: emrId,
      pet_id: petId,
      click_on_search_item: deleteAddon?.name,
      event_action: reason,
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      vms_user_id: userLogInfo,
      timestamp: Date.now(),
    };
    logAPI(logBody);

    onDeleteAddon?.(labId);
    setDeletedDiagnosticIds((previousIds) => [...previousIds, labId]);
    closeDeleteModal();
  };

  return (
    <div className="bg-white rounded-xl p-[10px] mt-2">
      <p className="text-[#1F1F23] font-semibold text-sm mb-2 px-1">
        Diagnostics{petName ? ` for ${petName}` : ""}
      </p>
      <AnimatePresence initial={false}>
        {visibleAddOns.map((addOn, index) => (
          <motion.div
            key={addOn.lab_test_id ?? index}
            initial={{ opacity: 0, y: -10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.55, ease: "easeInOut" }}
            className="py-2 px-3 border border-b flex justify-between items-center bg-white rounded-xl mb-2"
          >
            <p className="w-2/4 text-[#1F1F23] font-medium text-sm">
              {addOn.name}
            </p>
            <div className="flex gap-4 items-center">
              <button
                type="button"
                className={PRIMARY_BUTTON}
                onClick={() => onAddItem(addOn, "diagnostic_add_on")}
              >
                Add Item <MdOutlineKeyboardDoubleArrowUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="w-9 h-9 flex items-center justify-center bg-[#F2F2F2] rounded-[10px] hover:bg-red-50 transition-colors"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setDeleteAddon(addOn);
                  setDeleteReason("");
                  setDeleteError("");
                }}
                title="Delete Suggestion"
              >
                <TrashIcon className="h-5 w-5 text-[#ED0101]" />
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
      {deleteAddon && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          <div
            className="fixed inset-0 w-screen h-screen bg-gray-800 opacity-75 transition-opacity"
            onClick={closeDeleteModal}
          />
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left border border-gray-200 transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 w-full overflow-hidden z-10">
              <h3 className="text-base font-semibold leading-6 text-gray-900 flex justify-between items-center mb-4">
                <span>Delete Diagnostic Recommendation</span>
                <XMarkIcon
                  className="w-4 h-4 cursor-pointer text-gray-400 hover:text-gray-600"
                  onClick={closeDeleteModal}
                />
              </h3>
              <div className="mt-2">
                <p className="text-red-500 text-sm font-medium pb-4">
                  You are deleting recommendation <strong>{deleteAddon.name}</strong>. Please enter a reason to
                  continue.
                </p>
                <textarea
                  value={deleteReason}
                  onChange={(event) => {
                    setDeleteReason(event.target.value);
                    if (event.target.value.trim()) setDeleteError("");
                  }}
                  rows={2}
                  placeholder="Enter reason for deleting this suggestion"
                  className="w-full p-2 border border-gray-300 rounded-md text-sm outline-none"
                  autoFocus
                />
                {deleteError && <p className="text-xs text-red-500 mt-1 font-medium">{deleteError}</p>}
              </div>
              <div className="mt-5 left-0 sm:mt-4 sm:flex items-center sm:flex-row-reverse gap-1">
                <button
                  type="button"
                  disabled={!deleteReason.trim()}
                  onClick={handleDelete}
                  className="inline-flex w-full justify-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:bg-gray-400 sm:ml-3 sm:w-auto bg-red-600 hover:bg-red-500"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  className="mt-3 flex items-center w-full justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiagnosticAddOnsComponent;
